import { lazy } from 'react'
import { Navigate } from 'react-router-dom'
import AuthGuard from './components/AuthGuard'
import DashboardLayout from './components/dashboard/DashboardLayout'
import ActivePracticeGuard from './components/ActivePracticeGuard'
import GuestGuard from './components/GuestGuard'
import NoPracticeGuard from './components/NoPracticeGuard'
import FinancingDevGuard from './components/FinancingDevGuard'
import StripeCapitalGuard from './components/dashboard/guards/StripeCapitalGuard'
import StripePayoutsRedirectGuard from './components/dashboard/guards/StripePayoutsRedirectGuard'
import DefaultPayoutsRedirectGuard from './components/dashboard/guards/DefaultPayoutsRedirectGuard'
import Loadable from './components/Loadable'
import { lazyWithRetry } from 'util/lazy-with-retry'
import PaymentsGuard from './components/PaymentsGuard'
import RefundsGuard from './components/RefundsGuard'
import ConsolidatorFeatureFlagGuard from './components/dashboard/guards/ConsolidatorFeatureFlagGuard'
import IndependentRebatesGuard from './components/dashboard/guards/IndependentRebatesGuard'
import RebatesGuard from './components/dashboard/guards/RebatesGuard'
import ReadOnlyUserGuard from './components/dashboard/guards/ReadOnlyUserGuard/ReadOnlyUserGuard'
import AdminGuard from './components/dashboard/guards/AdminGuard'
import ScratchAdminGuard from './components/dashboard/guards/ScratchAdminGuard'
import StaffPaymentsOnlyGuard from './components/dashboard/guards/StaffPaymentsOnlyGuard'
import FeatureFlagGuard from './components/FeatureFlagGuard'
import { cardRequestsEnabled } from './components/dashboard/practices/helpers'
import { shouldShowConsolidatorPaymentHistory } from './util/consolidator'
import { isTerminalTippingEnabled } from './util/practice'
import PathPrefixRedirect from './components/PathPrefixRedirect'

// Authentication pages
const Login = Loadable(lazyWithRetry(() => import('./pages/authentication/Login')))
const PasswordReset = Loadable(lazyWithRetry(() => import('./pages/authentication/PasswordReset')))
const Register = Loadable(lazyWithRetry(() => import('./pages/authentication/Register')))

// Dashboard pages
const GettingStarted = Loadable(lazyWithRetry(() => import('./pages/dashboard/GettingStarted')))
const Account = Loadable(lazyWithRetry(() => import('./pages/dashboard/Account')))
const PracticeRequiredInfo = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/PracticeRequiredInfo'))
)

const TerminalPayment = Loadable(lazyWithRetry(() => import('./pages/dashboard/TerminalPayment')))
const TextToPay = Loadable(lazyWithRetry(() => import('./pages/dashboard/TextToPay')))
const FAQs = Loadable(lazyWithRetry(() => import('./pages/dashboard/FAQs')))
const Users = Loadable(lazyWithRetry(() => import('./pages/dashboard/Users/Users')))
const UserDetails = Loadable(lazyWithRetry(() => import('./pages/dashboard/Users/UserDetails')))
const Practices = Loadable(lazyWithRetry(() => import('./pages/dashboard/PracticesGQL')))
const PracticesV2 = Loadable(lazyWithRetry(() => import('./pages/dashboard/PracticesV2')))
const EditPracticeV2 = Loadable(lazyWithRetry(() => import('./pages/dashboard/EditPracticeV2')))
const PracticeDetails = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/PracticeDetailsGQL'))
)
const PracticeEdit = Loadable(lazyWithRetry(() => import('./pages/dashboard/PracticeEdit')))
const PayoutReportingV2 = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/PayoutReportingV2/PayoutReportingV2'))
)
const PayoutReportingEnterpriseV2 = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/PayoutReportingV2/PayoutReportingEnterpriseV2'))
)
const CapitalPayoutsEmbed = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/PayoutReportingV2/CapitalPayoutsEmbed'))
)
const CheckReporting = Loadable(lazyWithRetry(() => import('./pages/dashboard/CheckReporting')))
const ECheckLockboxEnterpriseReport = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/ECheckLockboxEnterpriseReport'))
)
const ECheckLockboxEnterpriseReportV2 = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/ECheckLockboxEnterpriseReportV2'))
)

const Payments = Loadable(lazyWithRetry(() => import('./pages/dashboard/Payments/Payments')))
const PaymentsSignup = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/Payments/PaymentsSignup'))
)
const AllPayments = Loadable(lazyWithRetry(() => import('./pages/dashboard/AllPayments')))
const AllRecurringPayments = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/AllRecurringPayments/AllRecurringPaymentsPage'))
)
const AllCashCounts = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/AllCashCounts/AllCashCountsPage'))
)
const PracticeCashCounts = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/AllCashCounts/PracticeCashCountsPage'))
)
const PaymentIntents = Loadable(lazyWithRetry(() => import('./pages/dashboard/PaymentIntents')))
const AllPaymentRequests = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/PaymentRequests/AllPaymentRequests'))
)
const BillingAdmin = Loadable(lazyWithRetry(() => import('./pages/dashboard/BillingAdmin/Billing')))
const PaymentRequests = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/PaymentRequests/PaymentRequests'))
)
const CardRequests = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/CardRequests/CardRequests'))
)
const PaymentDetails = Loadable(lazyWithRetry(() => import('./pages/dashboard/PaymentDetails')))
const Refunds = Loadable(lazyWithRetry(() => import('./pages/dashboard/Refunds')))
const RefundRequest = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/RefundRequest/RefundRequestReporting'))
)
const RepresentativeInfo = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/RepresentativeInfo'))
)
const Disputes = Loadable(lazyWithRetry(() => import('./pages/dashboard/Disputes')))
const DisputesV2 = Loadable(lazyWithRetry(() => import('./pages/dashboard/DisputesV2')))
const DisputeDetails = Loadable(lazyWithRetry(() => import('./pages/dashboard/DisputeDetails')))
const AllDisputes = Loadable(lazyWithRetry(() => import('./pages/dashboard/AllDisputes')))
const AdminAllDisputes = Loadable(lazyWithRetry(() => import('./pages/dashboard/AdminAllDisputes')))
const AllDisputesV2 = Loadable(lazyWithRetry(() => import('./pages/dashboard/AllDisputesV2')))
const AllPractices = Loadable(lazyWithRetry(() => import('./pages/dashboard/AllPractices')))
const Reconciliation = Loadable(lazyWithRetry(() => import('./pages/dashboard/Reconciliation')))
const CustomerDetails = Loadable(lazyWithRetry(() => import('./pages/dashboard/CustomerDetails')))
const CustomerEdit = Loadable(lazyWithRetry(() => import('./pages/dashboard/CustomerEdit')))
const CustomerList = Loadable(lazyWithRetry(() => import('./pages/dashboard/CustomerList')))
const InvoiceDetails = Loadable(lazyWithRetry(() => import('./pages/dashboard/InvoiceDetails')))
const Chat = Loadable(lazyWithRetry(() => import('./pages/communication/Chat/Chat')))
const ChatSettings = Loadable(
	lazyWithRetry(() => import('./pages/communication/Chat/ChatSettings'))
)
const CustomFormTemplate = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/CustomFormTemplate'))
)
const Appointments = Loadable(lazyWithRetry(() => import('./pages/dashboard/Appointments')))

const DailyTotals = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/DailyTotals/DailyTotals'))
)

const CashDrawerTotals = Loadable(
	lazyWithRetry(() => import('./pages/reporting/CashDrawerTotals/CashDrawerTotals'))
)

const MonthlyStatementList = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/MonthlyStatementList'))
)
const MonthlyStatementDetail = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/MonthlyStatementDetail'))
)
const HardwarePracticeSummaryInvoice = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/HardwarePracticeSummaryInvoice/index'))
)
const ConsDailyStatementList = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/ConsDailyStatementList'))
)
const HelpDesk = Loadable(lazyWithRetry(() => import('./pages/dashboard/HelpDesk')))

const ConsolidatorMonthlyReport = Loadable(
	lazyWithRetry(
		() => import('./pages/dashboard/consolidator-monthly-reports/ConsolidatorMonthlyReport')
	)
)

const AllConsolidatedPractices = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/ConsolidatedPractices/ConsolidatedPractices'))
)

const ConsolidatedPracticeFormHeader = Loadable(
	lazyWithRetry(
		() => import('./pages/dashboard/ConsolidatedPractices/ConsolidatedPracticeFormHeader')
	)
)

const EditConsolidatedPracticeV2 = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/ConsolidatedPractices/EditConsolidatedPracticeV2'))
)

const PIMSDataSync = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/PIMSDataSync/PIMSDataSync'))
)
const ProxyRouting = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/ProxyRouting/ProxyRouting'))
)
const FeatureFlagsPage = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/FeatureFlags/FeatureFlagsPage'))
)

const RefundAdmin = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/RefundAdmin/RefundAdmin'))
)

const CharityAccounts = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/CharityAccounts/CharityAccounts'))
)
const BusinessVerificationQueue = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/BusinessVerification/BusinessVerificationQueue'))
)
const BusinessVerificationDetail = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/BusinessVerification/BusinessVerificationDetail'))
)

// Error pages
const AuthorizationRequired = Loadable(
	lazyWithRetry(() => import('./pages/errors/AuthorizationRequired'))
)
const NotFound = Loadable(lazyWithRetry(() => import('./pages/errors/NotFound')))
const ServerError = Loadable(lazyWithRetry(() => import('./pages/errors/ServerError')))

const BankingInfo = Loadable(lazyWithRetry(() => import('./pages/settings/BankingInfo')))
const RequiredInfo = Loadable(lazyWithRetry(() => import('./pages/settings/RequiredInfo')))

const DynamicForm = Loadable(lazyWithRetry(() => import('./pages/DynamicForm')))
const PracticeSignUp = Loadable(
	lazyWithRetry(() => import('./pages/authentication/signup/PracticeSignUp'))
)
const PracticeSignUpV2 = Loadable(
	lazyWithRetry(() => import('./pages/authentication/signup-v2/PracticeSignUp'))
)
const TerminalOrders = Loadable(lazyWithRetry(() => import('./pages/dashboard/TerminalOrders')))

const ReadersReporting = Loadable(lazyWithRetry(() => import('./pages/dashboard/ReadersReporting')))
const TipReport = Loadable(lazyWithRetry(() => import('./pages/dashboard/TipReport')))

const AccountInvite = Loadable(lazyWithRetry(() => import('./pages/dashboard/AccountsInvitations')))

const SpecialNames = Loadable(lazyWithRetry(() => import('./pages/dashboard/SpecialNames')))

const LockboxChecks = Loadable(lazyWithRetry(() => import('./pages/dashboard/LockboxChecks')))

const Reminders = Loadable(lazyWithRetry(() => import('./pages/communication/Reminders/Reminders')))

const InstallmentPlans = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/InstallmentPlans/InstallmentPlans'))
)

const PastDueInstallmentPlans = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/PastDueInstallmentInvoices/PastDuePage'))
)

const OneTimeEmail = Loadable(
	lazyWithRetry(() => import('./pages/communication/OneTimeEmail/OneTimeEmail'))
)

const AppointmentRequests = Loadable(
	lazyWithRetry(() => import('./pages/communication/AppointmentRequests/AppointmentRequests'))
)

const Forms = Loadable(lazyWithRetry(() => import('./pages/communication/Forms/Forms')))

const PriorityCallList = Loadable(
	lazyWithRetry(() => import('./pages/communication/PriorityCallList/PriorityCallList'))
)

const ReputationManagement = Loadable(
	lazyWithRetry(() => import('./pages/communication/ReputationManagement/ReputationManagement'))
)

const OptOuts = Loadable(lazyWithRetry(() => import('./pages/communication/OptOuts/OptOuts')))

const StoredCards = Loadable(
	lazyWithRetry(() => import('./pages/reporting/StoredCards/StoredCards'))
)

const OptOutsPage = Loadable(lazyWithRetry(() => import('./pages/dashboard/optOuts/OptOutsPage')))

const ActionNeededV2 = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/ActionNeededV2/ActionNeededV2'))
)

const EnterpriseOpenActionItemsV2 = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/ActionNeededV2/EnterpriseOpenActionItemsV2'))
)

const GetStarted = Loadable(lazy(() => import('./pages/financing/GetStarted')))
const Training = Loadable(lazy(() => import('./pages/financing/Training')))
const FinancingAccount = Loadable(lazy(() => import('./pages/financing/Account')))
const FinancingPlans = Loadable(lazy(() => import('./pages/financing/Plans')))
const StripeCapitalPage = Loadable(lazy(() => import('./pages/financing/StripeCapital')))
const CapitalFAQPage = Loadable(lazy(() => import('./pages/financing/CapitalFAQPage')))

const CustomPayoutsPracticesList = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/CustomPayouts/PracticesListPage'))
)

const ManualPayoutBatchesPage = Loadable(
	lazyWithRetry(
		() => import('./pages/dashboard/CustomPayouts/ManualPayoutBatches/ManualPayoutBatchesPage')
	)
)

const PracticeFeeSettingsList = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/PracticeFeeSettings/PracticesListPage'))
)

const PracticeFeeLogsList = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/PracticeFeeLogs/PracticeFeeLogsListPage'))
)

const PracticeTerminalRebateSetup = Loadable(
	lazyWithRetry(
		() => import('./pages/dashboard/PracticeTerminalRebateSetup/PracticeTerminalRebateSetupPage')
	)
)

const PracticeTerminalRebateConfigPage = Loadable(
	lazyWithRetry(
		() => import('./pages/dashboard/PracticeTerminalRebateSetup/PracticeTerminalRebateConfigPage')
	)
)

const RebateProgramsListPage = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/RebatePrograms/RebateProgramsListPage'))
)

const IndustryPrograms = Loadable(lazyWithRetry(() => import('./pages/programs/Programs')))
const Rebates = Loadable(lazyWithRetry(() => import('./pages/rebates/Rebates')))
const RebatesReportingPage = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/RebatesReporting/RebatesReportingPage'))
)
const AllRebatesPage = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/RebatesReporting/AllRebates'))
)
const AllRebateHistoryPage = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/RebatesReporting/AllRebateHistory'))
)
const RebatePayoutSummaryPage = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/RebatesReporting/RebatePayoutSummary'))
)
const RebatePayoutDetailsPage = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/RebatesReporting/RebatePayoutDetails'))
)
const B2bRebateFilesSummaryPage = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/B2bRebateFiles/B2bRebateFilesSummary'))
)
const B2bRebateFileDetailsPage = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/B2bRebateFiles/B2bRebateFileDetails'))
)
const RebatesProductCatalog = Loadable(
	lazyWithRetry(() => import('./pages/dashboard/RebatesProductCatalog/RebatesProductCatalog'))
)
const RebatesBankingInfo = Loadable(
	lazyWithRetry(() => import('./pages/settings/RebatesBankingInfo'))
)

const routes = [
	{
		path: 'authentication',
		children: [
			{
				path: 'login',
				element: (
					<GuestGuard>
						<Login />
					</GuestGuard>
				)
			},
			{
				path: 'login-unguarded',
				element: <Login />
			},
			{
				path: 'password-reset',
				element: (
					<GuestGuard>
						<PasswordReset />
					</GuestGuard>
				)
			},
			{
				path: 'register',
				element: (
					<GuestGuard>
						<Register />
					</GuestGuard>
				)
			},
			{
				path: 'register-unguarded',
				element: <Register />
			}
		]
	},
	{
		path: 'NoPracticeGuard',
		children: [
			{
				path: 'no-practice-found',
				element: (
					<NoPracticeGuard>
						<GettingStarted />
					</NoPracticeGuard>
				)
			},
			{
				path: 'password-reset',
				element: (
					<NoPracticeGuard>
						<PasswordReset />
					</NoPracticeGuard>
				)
			},
			{
				path: 'register',
				element: (
					<NoPracticeGuard>
						<Register />
					</NoPracticeGuard>
				)
			}
		]
	},
	{
		path: 'dashboard',
		element: (
			<AuthGuard>
				<StaffPaymentsOnlyGuard>
					<DashboardLayout />
				</StaffPaymentsOnlyGuard>
			</AuthGuard>
		),
		children: [
			{
				path: '',
				element: <GettingStarted />
			},
			{
				path: 'payments',
				children: [
					{
						path: 'sign-up',
						element: <PaymentsSignup />
					},
					{
						path: ':channelFilterParam?/:dateParam?/:statusParam?',
						element: (
							<PaymentsGuard>
								<Payments />
							</PaymentsGuard>
						)
					}
				]
			},
			{
				path: 'payments-v2/*',
				element: <PathPrefixRedirect to="/dashboard/payments" />
			},
			{
				path: 'paymentIntents',
				children: [
					{
						path: '',
						element: (
							<PaymentsGuard>
								<PaymentIntents />
							</PaymentsGuard>
						)
					},
					{
						path: ':paymentIntentID',
						element: (
							<PaymentsGuard>
								<PaymentDetails />
							</PaymentsGuard>
						)
					}
				]
			},
			{
				path: 'invoices-v2/*',
				element: <PathPrefixRedirect to="/dashboard/invoices" />
			},
			{
				path: 'card-requests',
				element: (
					<PaymentsGuard>
						<FeatureFlagGuard check={cardRequestsEnabled}>
							<CardRequests />
						</FeatureFlagGuard>
					</PaymentsGuard>
				)
			},
			{
				path: 'chat',
				children: [
					{
						path: '',
						element: <Chat />
					},
					{
						path: 'new',
						element: <Chat />
					},
					{
						path: ':threadKey',
						element: <Chat />
					},
					{
						path: 'settings',
						element: <ChatSettings />
					},
					{
						path: 'template',
						element: <CustomFormTemplate />,
						children: [
							{
								path: 'new',
								element: <CustomFormTemplate />
							},
							{
								path: ':templateId',
								element: <CustomFormTemplate />
							}
						]
					},
					{
						path: 'snippets',
						element: <CustomFormTemplate />,
						children: [
							{
								path: 'new',
								element: <CustomFormTemplate />
							},
							{
								path: ':snippetId',
								element: <CustomFormTemplate />
							}
						]
					}
				]
			},
			{
				path: 'appointments',
				element: <Appointments />
			},
			{
				path: 'refunds',
				element: (
					<PaymentsGuard>
						<ReadOnlyUserGuard>
							<Refunds />
						</ReadOnlyUserGuard>
					</PaymentsGuard>
				)
			},
			{
				path: 'refund-requests',
				element: (
					<PaymentsGuard>
						<ReadOnlyUserGuard>
							<RefundRequest />
						</ReadOnlyUserGuard>
					</PaymentsGuard>
				)
			},
			{
				path: 'tip-report',
				element: (
					<PaymentsGuard>
						<FeatureFlagGuard
							check={(practice, consolidator) =>
								Boolean(isTerminalTippingEnabled(practice, consolidator))
							}
						>
							<TipReport />
						</FeatureFlagGuard>
					</PaymentsGuard>
				)
			},
			{
				path: 'representative-info',
				element: <RepresentativeInfo />
			},
			{
				path: 'disputes',
				children: [
					{
						path: '',
						element: (
							<PaymentsGuard>
								<Disputes />
							</PaymentsGuard>
						)
					},
					{
						path: ':disputeId',
						element: (
							<PaymentsGuard>
								<DisputeDetails />
							</PaymentsGuard>
						)
					}
				]
			},
			{
				path: 'disputes-v2',
				element: (
					<PaymentsGuard>
						<DisputesV2 />
					</PaymentsGuard>
				)
			},
			{
				path: 'checkReporting',
				children: [
					{
						path: '',
						element: (
							<PaymentsGuard>
								<CheckReporting />
							</PaymentsGuard>
						)
					}
				]
			},
			{
				path: 'echeck-lockbox-report',
				element: (
					<PaymentsGuard>
						<ECheckLockboxEnterpriseReport />
					</PaymentsGuard>
				)
			},
			{
				path: 'echeck-lockbox-report-v2',
				element: (
					<PaymentsGuard>
						<ECheckLockboxEnterpriseReportV2 />
					</PaymentsGuard>
				)
			},
			{
				path: 'allDisputes',
				element: (
					<PaymentsGuard>
						<AllDisputes />
					</PaymentsGuard>
				)
			},
			{
				path: 'all-disputes',
				element: (
					<PaymentsGuard>
						<AdminAllDisputes />
					</PaymentsGuard>
				)
			},
			{
				path: 'all-disputes-v2',
				element: (
					<PaymentsGuard>
						<AllDisputesV2 />
					</PaymentsGuard>
				)
			},
			{
				path: 'allPractices',
				element: <AllPractices />
			},
			{
				path: 'all-payments',
				element: (
					<PaymentsGuard>
						<FeatureFlagGuard
							check={(_, consolidator) => shouldShowConsolidatorPaymentHistory(consolidator)}
						>
							<AllPayments />
						</FeatureFlagGuard>
					</PaymentsGuard>
				)
			},
			{
				path: 'all-recurring-payments',
				element: (
					<PaymentsGuard>
						<AllRecurringPayments />
					</PaymentsGuard>
				)
			},
			{
				path: 'all-cash-counts',
				element: <AllCashCounts />
			},
			{
				path: 'all-invoices-v2/*',
				element: <PathPrefixRedirect to="/dashboard/all-invoices" />
			},
			{
				path: 'all-card-requests',
				element: (
					<PaymentsGuard>
						<FeatureFlagGuard
							check={(_, consolidator) => cardRequestsEnabled(undefined, consolidator)}
						>
							<CardRequests consolidatorView />
						</FeatureFlagGuard>
					</PaymentsGuard>
				)
			},
			{
				path: 'billing-admin',
				element: <BillingAdmin />
			},
			{
				path: 'hardware-invoice/:hardwarePracticeSummaryId',
				element: <HardwarePracticeSummaryInvoice />
			},
			{
				path: 'reconciliation',
				element: <Reconciliation />
			},
			{
				path: 'users',
				children: [
					{
						path: '',
						element: <Users />
					},
					{
						path: ':userID',
						element: (
							<ReadOnlyUserGuard>
								<UserDetails />
							</ReadOnlyUserGuard>
						)
					}
				]
			},
			{
				path: 'practices',
				children: [
					{
						path: '',
						element: (
							<AdminGuard>
								<Practices />
							</AdminGuard>
						)
					},
					{
						path: ':practiceID',
						element: (
							<AdminGuard>
								<PracticeDetails />
							</AdminGuard>
						)
					},
					{
						path: ':practiceID/edit',
						element: (
							<AdminGuard>
								<ReadOnlyUserGuard>
									<PracticeEdit />
								</ReadOnlyUserGuard>
							</AdminGuard>
						)
					}
				]
			},
			{
				path: 'practices-v2',
				children: [
					{
						path: '',
						element: (
							<AdminGuard>
								<PracticesV2 />
							</AdminGuard>
						)
					},
					{
						path: ':id',
						element: (
							<AdminGuard>
								<ReadOnlyUserGuard>
									<EditPracticeV2 />
								</ReadOnlyUserGuard>
							</AdminGuard>
						)
					}
				]
			},
			{
				path: 'payouts-v2',
				children: [
					{
						path: '',
						element: (
							<PaymentsGuard>
								<StripePayoutsRedirectGuard>
									<PayoutReportingV2 />
								</StripePayoutsRedirectGuard>
							</PaymentsGuard>
						)
					}
				]
			},
			{
				path: 'stripe-payouts',
				element: (
					<PaymentsGuard>
						<DefaultPayoutsRedirectGuard>
							<CapitalPayoutsEmbed />
						</DefaultPayoutsRedirectGuard>
					</PaymentsGuard>
				)
			},
			{
				path: 'allpayouts-v2',
				children: [
					{
						path: '',
						element: (
							<PaymentsGuard>
								<PayoutReportingEnterpriseV2 />
							</PaymentsGuard>
						)
					}
				]
			},
			{
				path: 'ActionNeeded',
				element: (
					<PaymentsGuard>
						<ActionNeededV2 />
					</PaymentsGuard>
				)
			},
			{
				path: 'open-action-items',
				element: (
					<PaymentsGuard>
						<EnterpriseOpenActionItemsV2 />
					</PaymentsGuard>
				)
			},
			{
				path: 'account',
				element: <Account />
			},
			{
				path: 'account/practice-required-info',
				element: <PracticeRequiredInfo />
			},
			{
				path: 'account/:tabParam',
				element: <Account />
			},
			{
				path: 'cash-counts',
				element: <PracticeCashCounts />
			},
			{
				path: 'collect-payment',
				element: (
					<ActivePracticeGuard>
						<PaymentsGuard>
							<RefundsGuard>
								<ReadOnlyUserGuard>
									<TerminalPayment />
								</ReadOnlyUserGuard>
							</RefundsGuard>
						</PaymentsGuard>
					</ActivePracticeGuard>
				)
			},
			{
				path: 'collect-payment/:tabParam',
				element: (
					<ActivePracticeGuard>
						<PaymentsGuard>
							<RefundsGuard>
								<ReadOnlyUserGuard>
									<TerminalPayment />
								</ReadOnlyUserGuard>
							</RefundsGuard>
						</PaymentsGuard>
					</ActivePracticeGuard>
				)
			},
			{
				path: 'text-to-pay',
				element: (
					<PaymentsGuard>
						<RefundsGuard>
							<ReadOnlyUserGuard>
								<TextToPay />
							</ReadOnlyUserGuard>
						</RefundsGuard>
					</PaymentsGuard>
				)
			},
			{
				path: 'ar',
				element: <Navigate to="/dashboard/invoices?type=accounts_receivable" replace />
			},
			{
				path: 'dailytotals',
				element: (
					<PaymentsGuard>
						<DailyTotals />
					</PaymentsGuard>
				)
			},
			{
				path: 'dailyTotals-v2/*',
				element: <PathPrefixRedirect to="/dashboard/dailytotals" />
			},
			{
				path: 'cash-drawer',
				element: (
					<PaymentsGuard>
						<CashDrawerTotals />
					</PaymentsGuard>
				)
			},
			{
				path: 'monthlytotals',
				element: (
					<PaymentsGuard>
						<MonthlyStatementList />
					</PaymentsGuard>
				)
			},
			{
				path: 'monthlytotal/:stmtId',
				element: (
					<PaymentsGuard>
						<MonthlyStatementDetail />
					</PaymentsGuard>
				)
			},
			{
				path: 'daily-feed',
				element: (
					<PaymentsGuard>
						<ConsDailyStatementList />
					</PaymentsGuard>
				)
			},
			{
				path: 'monthly-report',
				element: (
					<PaymentsGuard>
						<ConsolidatorMonthlyReport />
					</PaymentsGuard>
				)
			},
			{
				path: 'faq',
				element: <FAQs />
			},
			{
				path: 'customers',
				children: [
					{
						path: '',
						element: <CustomerList />
					},
					{
						path: ':customerId',
						element: <CustomerDetails />
					},
					{
						path: ':customerId/edit',
						element: <CustomerEdit />
					}
				]
			},
			{
				path: 'invoices',
				children: [
					{
						path: '',
						element: (
							<PaymentsGuard>
								<ReadOnlyUserGuard>
									<PaymentRequests />
								</ReadOnlyUserGuard>
							</PaymentsGuard>
						)
					},
					{
						path: ':invoiceId',
						element: (
							<PaymentsGuard>
								<ReadOnlyUserGuard>
									<InvoiceDetails />
								</ReadOnlyUserGuard>
							</PaymentsGuard>
						)
					}
				]
			},
			{
				path: 'all-invoices',
				element: (
					<PaymentsGuard>
						<AllPaymentRequests />
					</PaymentsGuard>
				)
			},
			{
				path: 'terminal-orders',
				children: [
					{
						path: '',
						element: (
							<PaymentsGuard>
								<TerminalOrders />
							</PaymentsGuard>
						)
					}
				]
			},
			{
				path: 'readers-reporting',
				children: [
					{
						path: '',
						element: (
							<PaymentsGuard>
								<ReadersReporting />
							</PaymentsGuard>
						)
					}
				]
			},
			{
				path: 'accounts',
				children: [
					{
						path: 'invitations',
						element: <AccountInvite />
					}
				]
			},
			{
				path: 'help-desk',
				element: <HelpDesk />
			},
			{
				path: 'installment-plans',
				element: (
					<PaymentsGuard>
						<InstallmentPlans />
					</PaymentsGuard>
				)
			},
			{
				path: 'past-due-plans',
				element: (
					<PaymentsGuard>
						<PastDueInstallmentPlans />
					</PaymentsGuard>
				)
			},
			{
				path: 'storedcard',
				element: (
					<PaymentsGuard>
						<StoredCards />
					</PaymentsGuard>
				)
			},
			{
				path: 'opt-outs',
				element: <OptOutsPage />
			},
			{
				path: 'custom-payouts',
				children: [
					{
						path: '',
						element: (
							<PaymentsGuard>
								<CustomPayoutsPracticesList />
							</PaymentsGuard>
						)
					},
					{
						path: 'manual-batches',
						element: (
							<ScratchAdminGuard>
								<ManualPayoutBatchesPage />
							</ScratchAdminGuard>
						)
					}
				]
			},
			{
				path: 'practice-fee-settings',
				element: <PracticeFeeSettingsList />
			},
			{
				path: 'practice-fee-logs',
				element: <PracticeFeeLogsList />
			},
			{
				path: 'rebate-programs',
				element: (
					<ScratchAdminGuard>
						<RebateProgramsListPage />
					</ScratchAdminGuard>
				)
			},
			{
				path: 'practice-terminal-rebate-setup',
				children: [
					{
						path: '',
						element: (
							<ScratchAdminGuard>
								<PracticeTerminalRebateSetup />
							</ScratchAdminGuard>
						)
					},
					{
						path: ':practiceId',
						element: (
							<ScratchAdminGuard>
								<PracticeTerminalRebateConfigPage />
							</ScratchAdminGuard>
						)
					}
				]
			},
			{
				path: 'all-rebates',
				element: <AllRebatesPage />
			},
			{
				path: 'all-rebate-history',
				element: (
					<ConsolidatorFeatureFlagGuard name="enableEnterpriseRebateHistory">
						<AllRebateHistoryPage />
					</ConsolidatorFeatureFlagGuard>
				)
			},
			{
				path: 'rebate-payouts',
				element: (
					<ConsolidatorFeatureFlagGuard name="enableRebatePayoutsPage">
						<RebatePayoutSummaryPage />
					</ConsolidatorFeatureFlagGuard>
				)
			},
			{
				path: 'rebate-payouts/:batchId',
				element: (
					<ConsolidatorFeatureFlagGuard name="enableRebatePayoutsPage">
						<RebatePayoutDetailsPage />
					</ConsolidatorFeatureFlagGuard>
				)
			},
			{
				path: 'b2b-rebate-files',
				element: (
					<ScratchAdminGuard>
						<B2bRebateFilesSummaryPage />
					</ScratchAdminGuard>
				)
			},
			{
				path: 'b2b-rebate-files/:id',
				element: (
					<ScratchAdminGuard>
						<B2bRebateFileDetailsPage />
					</ScratchAdminGuard>
				)
			},
			{
				path: 'rebates-product-catalog',
				children: [
					{
						path: '',
						element: <RebatesProductCatalog />
					},
					{
						path: ':tabParam',
						element: <RebatesProductCatalog />
					},
					{
						path: ':tabParam/:id',
						element: <RebatesProductCatalog />
					}
				]
			},
			{
				path: 'all-consolidated-practices',
				children: [
					{
						path: '',
						element: (
							<ScratchAdminGuard>
								<AllConsolidatedPractices />
							</ScratchAdminGuard>
						)
					},
					{
						path: 'add',
						element: (
							<ScratchAdminGuard>
								<ConsolidatedPracticeFormHeader />
							</ScratchAdminGuard>
						)
					},
					{
						path: ':id',
						element: (
							<ScratchAdminGuard>
								<EditConsolidatedPracticeV2 />
							</ScratchAdminGuard>
						)
					}
				]
			},
			{
				path: 'pims-data-sync',
				element: <PIMSDataSync />
			},
			{
				path: 'refund-admin',
				element: (
					<ScratchAdminGuard>
						<RefundAdmin />
					</ScratchAdminGuard>
				)
			},
			{
				path: 'charity-accounts',
				element: (
					<ScratchAdminGuard>
						<CharityAccounts />
					</ScratchAdminGuard>
				)
			},
			{
				path: 'proxy-routing',
				element: (
					<ScratchAdminGuard>
						<ProxyRouting />
					</ScratchAdminGuard>
				)
			},
			{
				path: 'feature-flags',
				element: (
					<ScratchAdminGuard>
						<FeatureFlagsPage />
					</ScratchAdminGuard>
				)
			},
			{
				path: 'business-verification',
				children: [
					{
						path: '',
						element: (
							<ScratchAdminGuard>
								<BusinessVerificationQueue />
							</ScratchAdminGuard>
						)
					},
					{
						path: ':submissionId',
						element: (
							<ScratchAdminGuard>
								<BusinessVerificationDetail />
							</ScratchAdminGuard>
						)
					}
				]
			}
		]
	},
	{
		path: 'communication',
		element: (
			<AuthGuard>
				<DashboardLayout />
			</AuthGuard>
		),
		children: [
			{
				path: 'reminders/:tab/',
				element: <Reminders />
			},
			{
				path: 'reminders/:tab/:clientId',
				element: <Reminders />
			},
			{
				path: 'one-time-email/:tab',
				element: <OneTimeEmail />
			},
			{
				path: 'appointment-requests',
				element: <AppointmentRequests />
			},
			{
				path: 'forms',
				element: <Forms />
			},
			{
				path: 'priority-call-list',
				element: <PriorityCallList />
			},
			{
				path: 'reputation-management',
				element: <ReputationManagement />
			},
			{
				path: 'opt-outs',
				element: <OptOuts />
			}
		]
	},
	{
		path: 'financing',
		element: (
			<AuthGuard>
				<DashboardLayout />
			</AuthGuard>
		),
		children: [
			{
				path: 'get-started',
				element: (
					<FinancingDevGuard>
						<GetStarted />
					</FinancingDevGuard>
				)
			},
			{
				path: 'training',
				element: (
					<FinancingDevGuard>
						<Training />
					</FinancingDevGuard>
				)
			},
			{
				path: 'account/:tabParam?',
				element: (
					<FinancingDevGuard>
						<FinancingAccount />
					</FinancingDevGuard>
				)
			},
			{
				path: 'plans',
				element: (
					<FinancingDevGuard>
						<FinancingPlans />
					</FinancingDevGuard>
				)
			},
			{
				path: 'capital',
				element: (
					<StripeCapitalGuard>
						<StripeCapitalPage />
					</StripeCapitalGuard>
				)
			},
			{
				path: 'capital/faq',
				element: (
					<StripeCapitalGuard>
						<CapitalFAQPage />
					</StripeCapitalGuard>
				)
			}
		]
	},
	{
		path: 'VendorNames',
		element: (
			<AuthGuard>
				<DashboardLayout />
			</AuthGuard>
		),
		children: [
			{
				path: '',
				element: <SpecialNames />
			}
		]
	},
	{
		path: 'LockboxChecks',
		element: (
			<AuthGuard>
				<DashboardLayout />
			</AuthGuard>
		),
		children: [
			{
				path: '',
				element: (
					<PaymentsGuard>
						<LockboxChecks />
					</PaymentsGuard>
				)
			}
		]
	},
	{
		path: 'settings',
		element: (
			<AuthGuard>
				<ActivePracticeGuard>
					<DashboardLayout />
				</ActivePracticeGuard>
			</AuthGuard>
		),
		children: [
			{
				path: 'banking-info',
				element: <BankingInfo />
			},
			{
				path: 'required-info',
				element: <RequiredInfo />
			}
		]
	},
	{
		path: 'forms',
		element: <DynamicForm />,
		children: [
			{
				path: ':templateParam',
				element: <DynamicForm />
			},
			{
				path: ':templateParam/:conversationParam',
				element: <DynamicForm />
			}
		]
	},
	{
		path: 'programs',
		element: (
			<AuthGuard>
				<DashboardLayout />
			</AuthGuard>
		),
		children: [
			{
				path: 'partnerships',
				element: <IndustryPrograms />
			}
		]
	},
	{
		path: 'rebates',
		element: (
			<AuthGuard>
				<DashboardLayout />
			</AuthGuard>
		),
		children: [
			{
				path: 'enrollment',
				element: (
					<RebatesGuard>
						<Rebates />
					</RebatesGuard>
				)
			},
			{
				path: 'reporting',
				element: (
					<RebatesGuard>
						<RebatesReportingPage />
					</RebatesGuard>
				)
			},
			{
				path: 'banking-info',
				element: (
					<IndependentRebatesGuard>
						<RebatesBankingInfo />
					</IndependentRebatesGuard>
				)
			}
		]
	},
	{
		path: 'signup',
		element: <PracticeSignUp />
	},
	{
		path: 'signup-v2',
		element: <PracticeSignUpV2 />
	},
	{
		path: '*',
		element: (
			<AuthGuard>
				<DashboardLayout />
			</AuthGuard>
		),
		children: [
			{
				path: '',
				element: <GettingStarted />
			},
			{
				path: '401',
				element: <AuthorizationRequired />
			},
			{
				path: '404',
				element: <NotFound />
			},
			{
				path: '500',
				element: <ServerError />
			},
			{
				path: '*',
				element: <NotFound />
			}
		]
	}
]

export default routes
